/**
 * Activity destination picker state (TRAV23 activities-front merge).
 *
 * Kept separate from `@share/store/slices/locations` rather than sharing it: the two
 * read different catalogues (`Urls.ActivityLocations` vs `Urls.Locations`) and not
 * every hotel destination has activity inventory, so the option lists are not
 * interchangeable and a shared `locations` array would show destinations that return
 * no results. The all-inclusive destination state has no activity equivalent and is
 * deliberately absent here.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { Canceler } from 'axios';

import { ILocation, LocationsTypes } from '@share/common-types';
import { Urls } from '@share/constants';
import { AppThunk, axiosInstance, getHeaders } from '@share/utils';
import { UNDEFINED_VALUE } from '@constants';

export interface IActivityLocationsState {
  locations: ILocation[];
  locationKey: string;
  loading: boolean;
  error: string;
  selectedLocationLabel: string;
  selectedLocation: ILocation | undefined;
  locationLabel: string;
  location: ILocation | undefined;
}

const initialState: IActivityLocationsState = {
  locations: [],
  locationKey: new Date().getTime().toString(),
  loading: false,
  error: '',
  selectedLocationLabel: UNDEFINED_VALUE,
  selectedLocation: undefined,
  locationLabel: UNDEFINED_VALUE,
  location: undefined,
};

const activityLocationsSlice = createSlice({
  name: 'activityLocations',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setLocations: (state, { payload }: PayloadAction<ILocation[]>) => {
      state.locations = payload;
    },
    setLocationKey: (state, { payload }: PayloadAction<string>) => {
      state.locationKey = payload;
    },
    setSelectLocation: (state, { payload }: PayloadAction<ILocation | undefined>) => {
      state.selectedLocation = payload;
    },
    setSelectLocationLabel: (state, { payload }: PayloadAction<string>) => {
      state.selectedLocationLabel = payload;
    },
    applyLocation: (state) => {
      state.locationLabel = state.selectedLocationLabel;
      state.location = state.selectedLocation;
    },
    resetLocation: (state) => {
      state.locations = [];
      state.loading = false;
      state.error = '';
      state.selectedLocationLabel = '';
      state.selectedLocation = UNDEFINED_VALUE;
      state.locationLabel = '';
      state.location = UNDEFINED_VALUE;
    },
    resetSelectedLocation: (state) => {
      state.selectedLocationLabel = state.locationLabel;
      state.selectedLocation = state.location;
    },
  },
});

export const activityLocationActions = activityLocationsSlice.actions;

export const activityLocationsReducer = activityLocationsSlice.reducer;

let cancelRequest: Canceler;

export const getActivityLocations = (
  search: string,
  selectedLocation: ILocation | undefined,
  onlyCities: boolean,
): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(activityLocationActions.setLoading(true));

    try {
      // Each keystroke supersedes the one before it.
      if (cancelRequest) {
        cancelRequest();
      }

      const { activityStore } = getState();
      const { isWidget } = activityStore;

      const res = await axiosInstance.get(Urls.ActivityLocations, {
        ...getHeaders(isWidget),
        params: {
          SearchString: search.trim(),
        },
        cancelToken: new axios.CancelToken((canceler: Canceler) => {
          cancelRequest = canceler;
        }),
      });

      let locations: ILocation[] = [...res.data.locations];

      // Keep an already-selected destination pinned at the top of its own result list.
      if (selectedLocation && search === selectedLocation.name) {
        locations = locations.filter(({ code }) => code !== selectedLocation.code);
        locations.unshift(selectedLocation);
      }

      if (onlyCities) {
        locations = locations.filter(({ type }) => type === LocationsTypes.Destination);
      }

      dispatch(activityLocationActions.setLoading(false));
      dispatch(activityLocationActions.setLocations(locations));
    } catch (error) {
      if (!(error instanceof axios.Cancel)) {
        console.error(error);
        dispatch(activityLocationActions.setLoading(false));
      }
    }
  };
};
